import { buildMiroFishSimulationAnalytics } from "@/lib/mirofish-simulation-analytics";
import type {
  MiroFishProjectRecord,
  MiroFishReportPayload,
  MiroFishReportResource,
  MiroFishReportTranslationPayload,
} from "@/types/mirofish";

const reportStatus = (
  project: MiroFishProjectRecord,
): MiroFishReportResource["status"] => {
  if (
    project.report.backendStatus === "failed" ||
    (project.status === "failed" && project.reportRequestedAt)
  ) {
    return "failed";
  }
  if (project.status === "report_ready") return "completed";
  if (
    project.status === "report_generating" ||
    project.report.backendStatus === "planning" ||
    project.report.backendStatus === "generating"
  ) {
    return "generating";
  }
  return "queued";
};

export function buildMiroFishReportResource(
  project: MiroFishProjectRecord,
  translation?: MiroFishReportTranslationPayload | null,
): MiroFishReportResource {
  const reportId = project.report.reportId || project.reportId;
  const simulationId =
    project.simulationId || project.simulation.simulationId || "";
  const status = reportStatus(project);
  const generationError =
    project.report.generationError ||
    (project.reportRequestedAt ? project.error : undefined);

  return {
    object: "report",
    schemaVersion: "report.v1",
    reportId,
    projectId: project.projectId,
    graphId: project.graphId,
    simulationId,
    status,
    progress: {
      percent: Math.max(
        0,
        Math.min(
          100,
          Number(
            project.report.generationProgress ??
              (status === "completed" ? 100 : 0),
          ) || 0,
        ),
      ),
      message: project.report.generationMessage || null,
      currentSection: project.report.currentSection || null,
      completedSections: project.report.completedSections || [],
    },
    content: {
      title: project.report.title,
      executiveSummary: project.report.executiveSummary,
      predictions: project.report.predictions,
      risks: project.report.risks,
      evidence: project.report.evidence,
      keyActors: project.report.keyActors,
      fullReport: project.report.fullReport,
      markdown: project.report.markdownContent || "",
    },
    aggregates:
      project.simulation.analytics ||
      buildMiroFishSimulationAnalytics({
        graphNodeCount: project.graph.nodeCount,
        simulation: project.simulation,
      }),
    requestedAt: project.reportRequestedAt || null,
    completedAt: project.reportReadyAt || null,
    error: generationError
      ? {
          code: project.report.generationErrorCode || project.errorCode || null,
          message: generationError,
        }
      : null,
    translation: translation ?? null,
    links: {
      self: `/api/mirofish/reports/${reportId}`,
      project: `/api/mirofish/projects/${project.projectId}`,
      graph: `/api/mirofish/graphs/${project.graphId}?projectId=${encodeURIComponent(project.projectId)}`,
      simulation: `/api/mirofish/simulations/${simulationId}`,
      sections: `/api/mirofish/reports/${reportId}/sections`,
      agentLog: `/api/mirofish/reports/${reportId}/agent-log`,
      chat: `/api/mirofish/reports/${reportId}/chat`,
    },
  };
}

export function reportResourceToPayload(
  resource: MiroFishReportResource,
): MiroFishReportPayload {
  return {
    reportId: resource.reportId,
    title: resource.content.title,
    executiveSummary: resource.content.executiveSummary,
    predictions: resource.content.predictions,
    risks: resource.content.risks,
    evidence: resource.content.evidence,
    keyActors: resource.content.keyActors,
    fullReport: resource.content.fullReport,
    markdownContent: resource.content.markdown,
    backendStatus: resource.status,
    generationError: resource.error?.message,
    generationErrorCode: resource.error?.code || undefined,
    generationProgress: resource.progress.percent,
    generationMessage: resource.progress.message || undefined,
    currentSection: resource.progress.currentSection || undefined,
    completedSections: resource.progress.completedSections,
    translation: resource.translation ?? undefined,
  };
}
